/**
 * SmartMatchEducationCard (iter1358)
 * ==================================
 *
 * Persistent, dismissible header card on Bank Feed Center that teaches
 * users the two-tier classification engine in plain language:
 *   1. Smart Match  — free, unlimited, rule + history based
 *   2. AI Assist    — capped per plan, kicks in only for the new stuff
 *
 * Automatically resurfaces (even if previously dismissed) when
 * `aiPaused` is true — i.e. the tenant is at their AI cap this cycle
 * and needs the reassurance that Smart Match keeps their books moving.
 */
import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Zap, Sparkles, X, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'smart_match_edu_dismissed_v1';

export const SmartMatchEducationCard = ({ aiPaused = false, onDismiss }) => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // If AI is paused this cycle, ignore prior dismissal — the user needs
    // to see this now more than ever.
    if (aiPaused) {
      setDismissed(false);
      return;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === '1') setDismissed(true);
    } catch { /* ignore */ }
  }, [aiPaused]);

  const handleDismiss = () => {
    if (aiPaused) return; // cannot dismiss while paused
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed) return null;

  return (
    <Card
      data-testid="smart-match-education-card"
      className={`mb-4 p-4 relative border-l-4 ${
        aiPaused
          ? 'border-l-amber-500 bg-gradient-to-br from-amber-50 via-card to-card'
          : 'border-l-[#283F8B] bg-gradient-to-br from-slate-50 via-card to-card'
      }`}
    >
      {!aiPaused && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={handleDismiss}
          aria-label="Dismiss"
          data-testid="smart-match-edu-close"
        >
          <X className="w-4 h-4" />
        </Button>
      )}

      <div className="pr-8">
        {aiPaused && (
          <p className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wide">
            AI Assist is paused for this cycle — but your books keep moving
          </p>
        )}
        <h3 className="text-sm font-semibold text-foreground mb-3">How AkountFly categorizes your transactions</h3>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-white/70 border border-slate-200">
            <div className="w-8 h-8 rounded-md bg-[#283F8B]/10 text-[#283F8B] flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground">1. Smart Match</p>
              <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                Free and unlimited. Categorizes anything it recognizes from your rules and past approvals — never runs out.
              </p>
            </div>
          </div>

          <div className={`flex items-start gap-3 p-3 rounded-lg bg-white/70 border ${aiPaused ? 'border-amber-200 opacity-70' : 'border-slate-200'}`}>
            <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${aiPaused ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'}`}>
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground">2. AI Assist {aiPaused && <span className="text-amber-700">(paused)</span>}</p>
              <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                {aiPaused
                  ? "Resumes automatically next cycle. Anything new waits in Review Needed — every approval teaches Smart Match."
                  : "Handles anything new. Capped per plan. Every approval teaches Smart Match, so AI Assist usage goes down over time."}
              </p>
            </div>
          </div>
        </div>

        {aiPaused && (
          <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1">
            <ChevronRight className="w-3 h-3" />
            Change your AI Assist limit anytime in <a href="/billing" className="underline">Plan &amp; Billing</a>.
          </p>
        )}
      </div>
    </Card>
  );
};

export default SmartMatchEducationCard;
